/* eslint-disable camelcase */
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  List,
  Typography,
} from '@material-ui/core'
import PropTypes, { arrayOf, shape } from 'prop-types'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import CheckoutLayout from '../../../layouts/buy/CheckoutLayout'
import { analyticsItemsFromCartItems } from '../../../lib/analytics/cart'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { NonEmptyCartContext } from '../../../lib/cart'
import { isShippingMethodEqual } from '../../../lib/cart/delivery'
import useCheckoutNavBarOptionValue from '../../../lib/cart/hooks/useCheckoutNavBarOptionValue'
import { usePageUrl } from '../../../lib/pages'
// import { logEvent } from '../../../utils/eventTools'
import { showHoldUntilMonday } from '../../../utils/buy/deliveryTools'
import Spinner from '../../Spinner'
import { GridButton } from '../shared/GridButton'
import DeliveryMethod from './DeliveryMethod'
import HeatSensitiveModal from './HeatSensitiveModal'
import useStyles from './styles/delivery'

/**
 * @param {NonNullable<CartFragment['shipping_addresses'][number]>['selected_shipping_method']} selectedMethod
 */
function shippingTierFromSelectedMethod(selectedMethod) {
  return selectedMethod
    ? `${selectedMethod.carrier_title} ${selectedMethod.method_title}`
    : undefined
}

/**
 * @param {CartFragment} cart
 */
function publishAddShippingInfoEvent(cart) {
  const currency = cart.prices?.grand_total?.currency
  const totalValue = cart.prices?.grand_total?.value
  if (currency == null || totalValue == null) {
    return
  }
  setAnalyticsEvent({
    event: 'add_shipping_info',
    currency,
    value: totalValue,
    coupon: cart.applied_coupons?.[0]?.code,
    shipping_tier: shippingTierFromSelectedMethod(
      cart.shipping_addresses[0]?.selected_shipping_method,
    ),
    items: analyticsItemsFromCartItems(cart.items ?? []),
  })
}

const Delivery = ({
  submitting,
  methods,
  defaultMethod,
  address,
  items,
  handleDeliveryMethodChange,
  handleHoldUntilMondayChange,
  defaultHoldUntilMonday,
}) => {
  // Temp until the `setShippingMethodsOnCart` is fixed
  const [availableMethods] = useState(methods)
  const { navBarOptionValue, loading: loadingNavBarOptions } =
    useCheckoutNavBarOptionValue('delivery')
  const [selectedMethod, setMethod] = useState(defaultMethod)
  const [holdUntilMonday, setHoldUntilMonday] = useState(defaultHoldUntilMonday)
  const [openHeatSensitiveModal, setOpenHeatSensitiveModal] = useState(false)
  const customerCart = useContext(NonEmptyCartContext)

  const hasShownHoldUntilMonday = useRef(false)

  const showHoldCheckbox =
    useMemo(() => showHoldUntilMonday({ address, items }), [address, items]) ||
    defaultHoldUntilMonday

  const holdUntilMondayCheckboxValue = holdUntilMonday ? 1 : 0

  const { navigate } = usePageUrl()

  useEffect(() => {
    if (showHoldCheckbox) {
      hasShownHoldUntilMonday.current = true
    }
  }, [showHoldCheckbox])

  const handleContinue = () => {
    // logEvent({ name: 'continue-to-payment-button' })
    publishAddShippingInfoEvent(customerCart)
    navigate('/checkout/payment')
  }

  const handleSubmit = () => {
    if (showHoldCheckbox && holdUntilMondayCheckboxValue === 0) {
      return setOpenHeatSensitiveModal(true)
    }

    return handleContinue()
  }

  // ensure shipping method is selected
  // - default shipping method to first available if none selected or selected method no longer
  //   available
  useEffect(() => {
    const [firstMethod] = methods
    if (
      !firstMethod ||
      (selectedMethod &&
        methods.some((method) => !!method && isShippingMethodEqual(method, selectedMethod)))
    ) {
      return
    }
    setMethod(firstMethod)
    handleDeliveryMethodChange(firstMethod)
  }, [defaultMethod, handleDeliveryMethodChange, methods, selectedMethod])

  const classes = useStyles()

  const handleSelectMethod = (method) => () => {
    setMethod(method)
    handleDeliveryMethodChange(method)
  }

  const handleSelectHoldUntilMonday = (checked) => {
    setHoldUntilMonday(checked)
    handleHoldUntilMondayChange(checked ? '1' : '0')
  }

  if (loadingNavBarOptions) return <Spinner loading />

  return (
    <CheckoutLayout checkoutNavigationBarTabValue={navBarOptionValue}>
      <Grid item xs={12}>
        <Typography variant="h5">DELIVERY METHOD</Typography>
      </Grid>
      <Grid className={classes.deliveryMethods} component={List} container item xs={12}>
        {availableMethods.reduce((acc, method) => {
          if (!method) return acc
          acc.push(
            <DeliveryMethod
              submitting={submitting}
              key={`${method.carrier_code}[${method.method_code}]`}
              method={method}
              handleSelectMethod={handleSelectMethod}
              checked={!!selectedMethod && isShippingMethodEqual(method, selectedMethod)}
            />,
          )
          return acc
        }, [])}
      </Grid>
      {(showHoldCheckbox || hasShownHoldUntilMonday.current) && (
        <Grid item xs={12}>
          <FormControlLabel
            classes={{ root: classes.holdUntilMondayCheckbox }}
            control={
              <Checkbox
                disabled={submitting}
                checked={holdUntilMonday}
                onChange={(e) => handleSelectHoldUntilMonday(e.target.checked)}
              />
            }
            label={
              <Typography variant="body1">
                <strong>Hold until Monday</strong>
                <div>
                  Your order includes perishable items. Check the box if you would like us to ship
                  these on a Monday so that they arrive during the week.
                </div>
              </Typography>
            }
            labelPlacement="end"
          />
        </Grid>
      )}
      <HeatSensitiveModal
        open={openHeatSensitiveModal}
        handleClose={() => setOpenHeatSensitiveModal(false)}
        handleContinue={handleContinue}
        disableButtons={submitting}
      />
      <GridButton
        onClick={handleSubmit}
        id="continue-to-payment-button"
        disabled={submitting || !selectedMethod}
        data-cy="go-to-payment"
      >
        {submitting ? <CircularProgress size={24} color="inherit" /> : 'CONTINUE TO PAYMENT'}
      </GridButton>
    </CheckoutLayout>
  )
}

Delivery.defaultProps = {
  defaultMethod: null,
}

Delivery.propTypes = {
  address: shape({
    country: shape({
      code: PropTypes.string,
    }),
    region: shape({
      code: PropTypes.string,
    }),
  }).isRequired,
  submitting: PropTypes.bool.isRequired,
  handleDeliveryMethodChange: PropTypes.func.isRequired,
  handleHoldUntilMondayChange: PropTypes.func.isRequired,
  defaultHoldUntilMonday: PropTypes.bool.isRequired,
  methods: arrayOf(
    shape({
      carrier_title: PropTypes.string,
      method_code: PropTypes.string,
      method_title: PropTypes.string,
      amount: shape({
        value: PropTypes.number,
      }),
    }),
  ).isRequired,
  defaultMethod: shape({
    carrier_title: PropTypes.string,
    method_code: PropTypes.string,
    method_title: PropTypes.string,
    amount: shape({
      value: PropTypes.number,
    }),
  }),
  items: arrayOf(
    shape({
      item_attributes: shape({
        heat_sensitive: PropTypes.number,
      }),
    }),
  ).isRequired,
}

export default Delivery
